import React from 'react';
import { 
    Paper, 
    Typography, 
    Grid, 
    Button,
    Card,
    CardContent,
    CardActions,
    Box,
    Avatar,
    Divider
} from '@mui/material';
import {
    DirectionsBoat as BoatIcon,
    Description as DocumentationIcon,
    AdminPanelSettings as AdminIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user, isAdmin } = useAuth();

    if (!user) {
        return (
            <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Please sign in to continue
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={() => navigate('/login')}
                    sx={{ mt: 2 }}
                >
                    Sign In
                </Button>
            </Paper>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Welcome Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                        src={user.photoURL || undefined}
                        sx={{ width: 56, height: 56, mr: 2 }}
                    >
                        {!user.photoURL && <PersonIcon sx={{ fontSize: 24 }} />}
                    </Avatar>
                    <Box>
                        <Typography variant="h4" gutterBottom>
                            Welcome, {user.name || user.displayName || 'User'}!
                            {isAdmin && (
                                <Box component="span" sx={{ ml: 2, px: 1.5, py: 0.5, bgcolor: 'primary.main', color: 'white', borderRadius: 1, fontSize: '0.875rem' }}>
                                    ADMIN
                                </Box>
                            )}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {user.email}
                        </Typography>
                        {user.company && (
                            <Typography variant="body2" color="text.secondary">
                                {user.company}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Typography variant="body1" color="text.secondary">
                    Choose an option below to get started with sail pricing, view API documentation, or manage system administration.
                </Typography>
            </Paper>

            {/* Main Menu Options */}
            <Grid container spacing={3}>
                {/* Sail Pricing */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <BoatIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                                <Typography variant="h5" component="h2">
                                    Sail Pricing
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Configure your boat details and get accurate sail pricing calculations. 
                                This includes mainsails, genoas, spinnakers, and all sail options.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • Enter boat specifications
                                • Configure sail measurements
                                • Get detailed pricing quotes
                                • Submit orders to Doyle Caribbean
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button 
                                size="large" 
                                variant="contained" 
                                color="primary"
                                fullWidth
                                startIcon={<BoatIcon />}
                                onClick={() => navigate('/boat-info')}
                            >
                                Start Sail Pricing
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* API Documentation */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <DocumentationIcon color="info" sx={{ fontSize: 40, mr: 2 }} />
                                <Typography variant="h5" component="h2">
                                    API Documentation
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Access comprehensive API documentation for developers integrating 
                                with the Doyle Caribbean sail pricing system.
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • API endpoint reference
                                • Authentication guides
                                • Request/response examples
                                • API key management
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Button 
                                size="large" 
                                variant="outlined" 
                                color="info"
                                fullWidth
                                startIcon={<DocumentationIcon />}
                                onClick={() => navigate('/api-docs')}
                            >
                                View API Documentation
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* API Administration - Admin Only */}
                {isAdmin && (
                    <Grid item xs={12}>
                        <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <AdminIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                                    <Typography variant="h5" component="h2" color="text.primary">
                                        API Administration
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Administrative interface for managing the API system, field definitions, 
                                    user sessions, and system monitoring. Admin access required.
                                </Typography>
                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            • Field definitions management
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            • API key management
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            • User session monitoring
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="body2" color="text.secondary">
                                            • System statistics
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <CardActions>
                                <Button 
                                    size="large" 
                                    variant="contained" 
                                    color="warning"
                                    fullWidth
                                    startIcon={<AdminIcon />}
                                    onClick={() => navigate('/admin')}
                                >
                                    Access API Administration
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};